<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import FormSelect from '@/Components/SelectInput.vue'
import SearchSelect from '@/Components/SearchSelect.vue'
import axios from 'axios'
import AddInventoryModal from '@/Components/Inventory/AddInventoryModal.vue'
import QRCode from 'qrcode'

const showAddModal = ref(false)

const props = defineProps({
    clients: Array,
    stylesByClient: Object,
})

const selectedClientId = ref(null)
const selectedStyleId = ref(null)
const loading = ref(false)
const details = ref(null)
const selectedBox = ref('')

const stylesForClient = computed(() => {
    if (selectedClientId.value == null) return []
    const key = String(selectedClientId.value)
    return props.stylesByClient?.[key] || props.stylesByClient?.[Number(key)] || []
})

watch(selectedClientId, () => {
    selectedStyleId.value = null
    details.value = null
    selectedBox.value = ''
})

const fetchDetails = async () => {
    if (!selectedClientId.value || !selectedStyleId.value) return
    const url = route('admin.create-label.selection')
    const { data } = await axios.get(url, {
        params: {
            clientId: Number(selectedClientId.value),
            styleId: Number(selectedStyleId.value),
        },
    })
    details.value = data
    console.log('Fetched details:', data)
    const stillExists = boxOptions.value.some(o => o.value === selectedBox.value)
    if (!stillExists) selectedBox.value = ''
}

async function onContinue() {
    if (!selectedClientId.value || !selectedStyleId.value) return
    loading.value = true
    try { await fetchDetails() } finally { loading.value = false }
}

const boxOptions = computed(() => {
    if (!details.value?.boxMetadata) return []
    const seen = new Set()
    const opts = []
    for (const color of Object.keys(details.value.boxMetadata)) {
        for (const box of Object.keys(details.value.boxMetadata[color])) {
            if (!seen.has(box)) {
                seen.add(box)
                opts.push({ value: box, label: `${box} (${color})` })
            }
        }
    }
    return opts.sort((a, b) =>
        a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: 'base' })
    )
})

const boxUrl = computed(() => {
    if (!details.value?.style?.styleId || !selectedBox.value) return ''
    const styleId = details.value.style.styleId
    return `http://internal.uniformsourcing.com/inventory/${styleId}/report#boxNum=${encodeURIComponent(selectedBox.value)}`
})

const onModalClose = async () => {
    showAddModal.value = false
    await fetchDetails()
}

const selectedBoxMeta = computed(() => {
    if (!details.value?.boxMetadata || !selectedBox.value) return null
    const meta = details.value.boxMetadata
    for (const color of Object.keys(meta)) {
        if (meta[color]?.[selectedBox.value]) {
            return { color, ...meta[color][selectedBox.value] }
        }
    }
    return null
})

const qrDataUrl = ref('')
const qrLoading = ref(false)

async function buildQr() {
    qrDataUrl.value = ''
    if (!boxUrl.value) return
    try {
        qrLoading.value = true
        qrDataUrl.value = await QRCode.toDataURL(boxUrl.value, {
            errorCorrectionLevel: 'L',
            width: 512,
            margin: 0
        })
    } catch (e) {
        console.error('QR build error:', e)
        qrDataUrl.value = ''
    } finally {
        qrLoading.value = false
    }
}

watch(boxUrl, () => { buildQr() })

watch(details, () => { buildQr() })

onMounted(() => { buildQr() })

const locationDisplay = computed(() => {
    const m = selectedBoxMeta.value
    if (!m) return ''
    const name = m.storageName || m.locationIdentifier || ''
    const idf = m.locationIdentifier || ''
    const slot = m.conveyorSlot ? ` (Slot: ${m.conveyorSlot})` : ''
    return `${idf}_${name}${slot}`
})

const styleNumber = computed(() => details.value?.style?.styleNumber || '')
const garmentName = computed(() => details.value?.style?.garmentName || '')
const colorName = computed(() => selectedBoxMeta.value?.color || '')
const sizesText = computed(() => {
    const arr = details.value?.mainSizes || []
    return Array.isArray(arr) && arr.length ? arr.join(', ') : ''
})
const initialQty = computed(() => selectedBoxMeta.value?.totalQty ?? '')

const clientLogoUrl = computed(() => details.value?.client?.logoUrl)
const garmentImageUrl = computed(() => {
    const img = details.value?.style?.image
    return img ? `/content/uploadFiles/inventory/images/${img}` : ''
})

const colorImageUrl = computed(() => {
    const color = colorName.value
    if (!color) return ''

    const byName = details.value?.colorImagesByName || details.value?.colorImageByColor
    if (byName && typeof byName === 'object' && byName[color]) {
        return byName[color]
    }

    const options = details.value?.colorOptions || []
    const idx = options.indexOf(color)
    const imgs = details.value?.colorImages || []
    const img = idx >= 0 ? imgs[idx] : null

    console.log(img || garmentImageUrl.value || '')

    return img || garmentImageUrl.value || ''
})

const qrImgUrl = computed(() =>
    boxUrl.value
        ? `https://chart.googleapis.com/chart?cht=qr&chs=320x320&chld=L|0&chl=${encodeURIComponent(boxUrl.value)}`
        : ''
)

const labelRef = ref(null)

const openPrint = async () => {
    if (!labelRef.value) return
    await nextTick()

    const html = labelRef.value.innerHTML

    const w = window.open('', 'label', 'width=800,height=600')
    if (!w) return

    const css = `
    * { font-family: Helvetica, Arial, sans-serif; }
    body { margin:0; padding:12px; }
    #label { margin:auto; position:relative; max-height:350px; display:flex; flex-direction:column; justify-content:center; overflow:auto; }
    .row-top, .row-bottom { display:flex; justify-content:space-between; align-items:center; width:100%; }
    .lhs { display:flex; align-items:center; gap:8px; }
    #style { font-size:190px; text-align:right; margin-left:5px; line-height:0.8; }
    b { font-weight:700; }
    img { object-fit:contain; }
  `

    w.document.write(`
    <html>
      <head>
        <title>Label</title>
        <meta charset="utf-8" />
        <style>${css}</style>
      </head>
      <body>${html}</body>
    </html>
  `)
    w.document.close()

    const imgs = w.document.images
    const promises = []
    for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i]
        if (img.complete) continue
        promises.push(new Promise(res => { img.onload = img.onerror = () => res() }))
    }
    await Promise.all(promises)

    w.focus()
    w.print()
    w.close()
}

const clientOptions = computed(() =>
    (props.clients || [])
        .filter((client) => (client?.id != null || client?.ID != null) && (client?.name || client?.client))
        .map((client) => ({
            value: Number(client.id ?? client.ID),
            label: String(client.name ?? client.client ?? ''),
        }))
        .filter((option) => option.label && !Number.isNaN(option.value))
)

const styleOptions = computed(() =>
    stylesForClient.value.map((style) => ({
        value: Number(style.styleId),
        label: String(style.styleNumber ?? ''),
    })).filter((option) => option.label && !Number.isNaN(option.value))
)

const clientModel = computed({
    get: () => (selectedClientId.value != null ? Number(selectedClientId.value) : null),
    set: (value) => {
        selectedClientId.value = value != null ? Number(value) : null
    },
})

const styleModel = computed({
    get: () => (selectedStyleId.value != null ? Number(selectedStyleId.value) : null),
    set: (value) => {
        selectedStyleId.value = value != null ? Number(value) : null
    },
})
</script>

<template>
    <AppLayout title="Create Label">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">Create Label</h2>
        </template>

        <div class="max-w-2xl mx-auto p-6 space-y-6">
            <div class="space-y-4 rounded p-4 shadow">
                <div>
                    <label class="block text-sm font-medium">Client</label>
                    <SearchSelect v-model="clientModel" :options="clientOptions" placeholder="-- Select Client --"
                        class="mt-1 w-full" :clearable="true" />
                </div>

                <div>
                    <label class="block text-sm font-medium">Style</label>
                    <SearchSelect v-model="styleModel" :options="styleOptions" :disabled="selectedClientId == null"
                        placeholder="-- Select Style --" class="mt-1 w-full" :clearable="true" />
                </div>

                <PrimaryButton class="px-4 py-2 border rounded bg-blue-600 text-white disabled:opacity-50"
                    :disabled="selectedClientId == null || selectedStyleId == null || loading" @click="onContinue">
                    {{ loading ? 'Loading…' : 'Continue' }}
                </PrimaryButton>
            </div>

            <div v-if="details" class="space-y-4 rounded p-4 shadow">
                <h2 class="text-lg font-medium">Selected Details</h2>
                <div><b>Client:</b> {{ details.client?.name }}</div>
                <div><b>Style Number:</b> {{ details.style?.styleNumber }}</div>

                <div v-if="details.client?.logoUrl">
                    <b>Client logo:</b><br />
                    <img :src="clientLogoUrl" alt="Client logo" width="150" />
                </div>

                <PrimaryButton class="mt-4" @click="showAddModal = true">
                    Add Inventory
                </PrimaryButton>

                <h2 class="text-lg font-medium">Information for Label</h2>

                <div class="mt-6">
                    <label class="block text-sm font-medium">Select Box</label>
                    <FormSelect v-model="selectedBox" class="mt-1 w-full" :disabled="!details || !details.boxMetadata">
                        <option value="">-- Select Box --</option>
                        <option v-for="opt in boxOptions" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                        </option>
                    </FormSelect>

                    <div v-if="boxUrl" class="mt-3 text-sm">
                        <span class="font-medium">Box URL: </span>
                        <a :href="boxUrl" target="_blank" rel="noopener" class="text-blue-600 underline break-all">
                            {{ boxUrl }}
                        </a>
                    </div>
                </div>

                <PrimaryButton class="mt-4" :disabled="!selectedBox || !boxUrl" @click="openPrint">
                    Create Label
                </PrimaryButton>
            </div>
        </div>
    </AppLayout>

    <AddInventoryModal v-if="details" :show="showAddModal" @close="onModalClose" :style-id="details.style?.styleId"
        :style-number="details.style?.styleNumber || ''" :garment-type="details.style?.garmentName || ''"
        :gender="details.style?.sex || ''" :client="details.client?.name || ''"
        :color-options="details.colorOptions || []" :storages="details.storages || []"
        :main-sizes="details.mainSizes || []" :opt-sizes="details.optSizes || []" />

    <div ref="labelRef" style="display:none;">
        <div id="label"
            style="margin:auto; position: relative; max-height: 350px; overflow: auto; display: flex; flex-direction: column; justify-content: center;">

            <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                <div style="display: flex; flex-direction: column; align-items: center; width: 80%;">
                    <div id="clientLogoContainerLabel" style="text-align: center; margin-top: 5px; padding: 4px;">
                        <img v-if="clientLogoUrl" :src="clientLogoUrl"
                            style="width: 100%; height: auto; max-width:180px; max-height: 60px;" />
                    </div>

                    <div style="display: flex; align-items:center; justify-content: flex-end; margin-top: 32px;">
                        <span style="margin-right: 2rem;">{{ locationDisplay }}</span>
                        Row:<span style="margin: 0 0.5rem 0 0.25rem;">{{ selectedBoxMeta?.row || '-' }}</span>
                        Rack:<span style="margin: 0 0.5rem 0 0.25rem;">{{ selectedBoxMeta?.rack || '-' }}</span>
                        Shelf:<span style="margin: 0 0.5rem 0 0.25rem;">{{ selectedBoxMeta?.shelf || '-' }}</span>
                    </div>
                </div>

                <img v-if="colorImageUrl" :src="colorImageUrl" width="100" height="100" style="object-fit: contain;"
                    onerror="this.style.display='none';" />

                <div style="margin-left: 20px; text-align: left;">
                    <b>Style:</b> <span>{{ styleNumber }}</span><br>
                    <b>Color:</b> <span>{{ colorName }}</span><br>
                    <b>Description:</b> <span>{{ garmentName }}</span><br>
                    <b style="font-size: 22px">Sizes:</b> <span style="font-size: 22px">{{ sizesText }}</span><br>
                    <b>Initial Qty:</b> <span>{{ initialQty }}</span><br>
                    <input type="hidden" />
                </div>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                <div id="qrcode">
                    <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR" style="width:170px; height:170px;" />
                    <span v-else-if="qrLoading" style="font-size:12px;">Generating QR…</span>
                    <span v-else style="font-size:12px;">No QR</span>
                </div>
                <div id="style" style="font-size: 190px; text-align: right; margin-left: 5;">
                    <span>{{ selectedBox }}</span>
                </div>
            </div>

            <input id="url" type="hidden" style="width:80%" /><br />
        </div>
    </div>
</template>